import React, { useState } from "react";
import { List, notification, Space } from "antd";
import {
  dateAndTime,
  getLocalTimeZoneDateFormat,
} from "../../../utils/dateutils";
import SmartIcon from "../../../components/generic/genericIcon/SmartIcon";
import apiService from "../../../service/apiservice/apiService";
import { useReadMemory } from "../../generic/hooks/useReadMemory";
import MemoryType from "../../../service/memory/enum/MemoryType";
import { useSetRecoilState } from "recoil";
import {
  notificationModal,
  refreshNotifications,
} from "../../../components/recoil/notificationsRecoil";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { useNavigate } from "react-router";
import { RoleToLinkMapper } from "../../utils/UrlUtils";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import { strings } from "../../utils/strings";
import { publishEvent } from "../../../components/generic/EventPublisher";
import { Subject } from "rxjs";
import { EventType } from "../../../service/network/utils/EventType";
import { statusCode } from "../../utils/statuscodes";
import { getFullName } from "../../careprovider/visits/utils";
import moment from "moment";
import NotificationSlots from "./NotificationSlots";

export const carePlanUpdateResponse = new Subject();

const NotificationCard = ({ data }) => {
  const user = useReadMemory(MemoryType.USER);
  const organizationIdentifier = useGetOrgIdentifier();
  const setRefresh = useSetRecoilState(refreshNotifications);
  const setModal = useSetRecoilState(notificationModal);
  const navigate = useNavigate();
  const [loadingButtons, setLoadingButtons] = useState({});
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);
  const parsedMessage = JSON.parse(data?.message || "{}");
  const metadata = JSON.parse(data?.metadata || "{}");

  const sendNotification = async (careplan, action) => {
    const services = JSON.parse(careplan?.services || "[]");
    const serviceString = services
      .map((item) => JSON.parse(item).title)
      .join(", ");

    const message = {
      type: "text",
      message: `${getFullName(
        user?.givenName,
        user?.familyName,
      )} has ${action} request for ${serviceString}${
        careplan?.startDate
          ? ` from ${moment(careplan.startDate).format(getLocalTimeZoneDateFormat())}`
          : ""
      }.`,
      actions: [
        {
          label: "View Details",
          actionType: "redirect",
          metadata: {
            url: `request-for-volunteering-service?carePlanId=${careplan?.id}`,
          },
        },
        {
          label: "View Profile",
          actionType: "redirect",
          metadata: {
            url: `volunteer-profile?identifier=${user?.userIdentifier}`,
          },
        },
      ],
    };

    const payload = {
      sender: user?.userIdentifier,
      senderType: "workforce",
      message: JSON.stringify(message),
      metadata: "",
      organizationIdentifier,
      recipients: [
        {
          recipient: careplan?.coordinatorIdentifier,
          recipientType: "workforce",
          metadata: "",
        },
      ],
      keywords: [
        {
          keyword: "notification",
          metadata: "",
        },
        {
          keyword: `careplan_${careplan?.id}`,
          metadata: "",
        },
      ],
    };
    await apiService.post(
      "niramayafoundation/api/v1/communication/message",
      payload,
    );
  };

  const notifyAssignmentFull = async (carePlan, sharedOpportunities) => {
    const recipients = sharedOpportunities
      ?.filter(
        (item) => item?.workforce?.userIdentifier !== user?.userIdentifier,
      )
      .map((ele) => ({
        recipient: ele?.workforce?.userIdentifier,
        recipientType: "workforce",
        metadata: "",
      }));
    const message = {
      type: "text",
      message: `Patient assignment request for ${moment(
        carePlan?.startDate,
      ).format(
        getLocalTimeZoneDateFormat(),
      )} has already been accepted by other volunteers. We’ll notify you of new opportunities.`,
      actions: [],
    };

    const payload = {
      sender: "system",
      senderType: "system",
      message: JSON.stringify(message),
      metadata: "",
      organizationIdentifier,
      recipients,
      keywords: [
        {
          keyword: "notification",
          metadata: "",
        },
        {
          keyword: `careplan_${carePlan?.id}`,
          metadata: "",
        },
      ],
    };
    await apiService.post(
      "niramayafoundation/api/v1/communication/message",
      payload,
    );
  };

  const handleAction = async (
    payload,
    assignedOpportunities,
    carePlan,
    sharedOpportunities,
  ) => {
    const isDismiss =
      metadata?.acceptanceLimit &&
      assignedOpportunities + 1 === metadata?.acceptanceLimit;

    if (isDismiss) {
      await notifyAssignmentFull(carePlan, sharedOpportunities);
    }

    const url = `niramayafoundation/api/v1/communication/message/actions?messageId=${
      data?.id
    }${isDismiss ? "&dismiss=true" : ""}`;
    const response = await apiService.post(url, payload);

    if (response?.length) {
      setRefresh((prev) => !prev);
    }
  };

  const handleDismisClick = async () => {
    const payload = [
      {
        actionType: "read",
        value: "true",
        actionOwnerType: "workforce",
        actionOwner: user?.userIdentifier,
      },
    ];

    handleAction(payload);
  };

  const getCarePlanDetails = async (carePlanId) => {
    const endpoint = "niramayafoundation/niramayavisits/api/v1/careplans";
    const params = {
      organizationIdentifier,
      carePlanId,
    };
    const response = await apiService.get(endpoint, params);
    if (response[0]?.id) {
      return response[0];
    }
  };

  const errorMessages = {
    [statusCode.REQUEST_ACCEPTANCE_LIMIT_REACHED]:
      strings.REQUEST_ACCEPTANCE_LIMIT_REACHED,
    [statusCode.OUTDATED_DATA]: strings.OUTDATED_DATA,
  };

  const handleAcceptCareplan = async (item) => {
    const carePlanId = item?.metadata?.carePlanId;
    if (!carePlanId) return;

    const loadingKey = item.label;
    setLoadingButtons((prev) => ({ ...prev, [loadingKey]: true }));

    const payload = [
      {
        actionType: "read",
        value: "true",
        actionOwnerType: "workforce",
        actionOwner: user?.userIdentifier,
      },
      {
        actionType: "carePlanAccepted",
        value: "true",
        actionOwnerType: "workforce",
        actionOwner: user?.userIdentifier,
      },
    ];

    const carePlan = await getCarePlanDetails(item?.metadata?.carePlanId);

    let updatedFrequency = null;
    const frequencyData = JSON.parse(carePlan?.frequency || "[]");

    if (
      carePlan?.frequencyPeriod === "slot based" &&
      frequencyData?.[0]?.slots
    ) {
      const slotData = frequencyData[0];
      const updatedSlots = slotData.slots.map((slot) => {
        if (selectedSlotIds.includes(slot.id)) {
          return {
            ...slot,
            assignedVolunteer: {
              givenName: user?.givenName,
              familyName: user?.familyName,
              email: user?.email,
              gender: user?.gender,
              jobTitle: user?.jobTitle,
              telephone: user?.telephone,
              birthDate: user?.birthDate,
              userIdentifier: user?.userIdentifier,
              latitude: user?.latitude,
              longitude: user?.longitude,
              isActive: user?.isActive,
              organizationIdentifier: user?.organizationIdentifier,
              addressLocality: user?.addressLocality,
              addressRegion: user?.addressRegion,
              completeAddress: user?.completeAddress,
              addressCountry: user?.addressCountry,
              streetAddress: user?.streetAddress,
              profilePicture: user?.profilePicture,
              workforceStatus: user?.workforceStatus,
              teams: user?.teams,
            },
          };
        }
        return slot;
      });

      const newFrequencyData = [
        {
          ...slotData,
          slots: updatedSlots,
        },
      ];

      updatedFrequency = JSON.stringify(newFrequencyData);
    }

    const opportunities = carePlan?.opportunities?.map((opportunity) =>
      opportunity?.workforceIdentifier === user?.userIdentifier
        ? {
            ...opportunity,
            opportunityStatus: "Assigned",
            frequency: updatedFrequency || carePlan?.frequency,
          }
        : opportunity,
    );

    const assignedOpportunities = carePlan?.opportunities?.filter(
      (opportunity) => opportunity?.opportunityStatus === "Assigned",
    );

    const sharedOpportunities = carePlan?.opportunities?.filter(
      (opportunity) => opportunity?.opportunityStatus === "Shared",
    );

    const response = await apiService.post(
      "niramayafoundation/niramayavisits/api/v1/careplan",
      {
        ...carePlan,
        id: carePlanId,
        opportunities,
        status: "Active",
        frequency: updatedFrequency || carePlan?.frequency,
      },
    );
    if (response?.id) {
      publishEvent("niramaya.volunteer.request.accepted", user, carePlan);
      handleAction(
        payload,
        assignedOpportunities?.length,
        carePlan,
        sharedOpportunities,
      );
      sendNotification(carePlan, "accepted");
      carePlanUpdateResponse.next({
        type: EventType.CAREPLAN_ACCEPTED,
      });
      notification.success({
        message: strings.ACCEPT_OPPORTUNITY_SUCCESS,
        placement: "bottomRight",
      });
    } else if (response?.code) {
      setRefresh((prev) => !prev);
      notification.error({
        message: errorMessages[response?.code],
        placement: "bottomRight",
      });
    } else {
      notification.error({
        message: strings.ACCEPT_OPPORTUNITY_ERROR,
        placement: "bottomRight",
      });
    }
    setLoadingButtons((prev) => ({ ...prev, [loadingKey]: false }));
  };

  const handleRejectCareplan = async (item) => {
    const carePlanId = item?.metadata?.carePlanId;
    if (!carePlanId) return;

    const loadingKey = item.label;
    setLoadingButtons((prev) => ({ ...prev, [loadingKey]: true }));

    const payload = [
      {
        actionType: "read",
        value: "true",
        actionOwnerType: "workforce",
        actionOwner: user?.userIdentifier,
      },
      {
        actionType: "carePlanRejected",
        value: "true",
        actionOwnerType: "workforce",
        actionOwner: user?.userIdentifier,
      },
    ];

    const carePlan = await getCarePlanDetails(item?.metadata?.carePlanId);
    const opportunities = carePlan?.opportunities?.map((opportunity) =>
      opportunity?.workforceIdentifier === user?.userIdentifier
        ? { ...opportunity, opportunityStatus: "Rejected" }
        : opportunity,
    );

    const response = await apiService.post(
      "niramayafoundation/niramayavisits/api/v1/careplan",
      { ...carePlan, id: carePlanId, opportunities },
    );
    if (response?.id) {
      publishEvent("niramaya.volunteer.request.rejected", user, carePlan);
      handleAction(payload);
      sendNotification(carePlan, "declined");
      notification.success({
        message: strings.REJECT_OPPORTUNITY_SUCCESS,
        placement: "bottomRight",
      });
    } else if (response?.code) {
      setRefresh((prev) => !prev);
      notification.error({
        message: errorMessages[response?.code],
        placement: "bottomRight",
      });
    } else {
      notification.error({
        message: strings.REJECT_OPPORTUNITY_ERROR,
        placement: "bottomRight",
      });
    }
    setLoadingButtons((prev) => ({ ...prev, [loadingKey]: false }));
  };

  const handleButtonClick = (item) => {
    const actions = {
      redirect: () => {
        navigate(RoleToLinkMapper(user?.jobTitle) + item?.metadata?.url);
      },
      redirectToCarePlan: () => {
        const url = `${item?.metadata?.url}&messageId=${data?.id}`;
        navigate(RoleToLinkMapper(user?.jobTitle) + url);
      },
      acceptCarePlan: () => {
        handleAcceptCareplan(item);
      },
      rejectCarePlan: () => {
        handleRejectCareplan(item);
      },
    };

    if (actions[item.actionType]) {
      actions[item.actionType]();
      setModal(false);
    }
  };

  const handleSlotClick = (slot) => {
    setSelectedSlotIds((prev) => {
      if (prev.includes(slot.id)) {
        return prev.filter((id) => id !== slot.id);
      } else {
        return [...prev, slot.id];
      }
    });
  };

  return (
    <>
      <List.Item>
        <div className="notification-bubble">
          <div className="notification-text-wrapper">
            <div className="notification-text">{parsedMessage?.message}</div>
            {parsedMessage?.dismissible !== false && (
              <div>
                <SmartIcon
                  iconKey="reject"
                  width="16"
                  height="16"
                  handleIconClick={handleDismisClick}
                />
              </div>
            )}
          </div>
          {metadata?.frequency &&
            metadata?.frequencyPeriod === "slot based" && (
              <NotificationSlots
                frequency={metadata?.frequency}
                onSlotClick={(slot) => handleSlotClick(slot)}
                selectedSlotIds={selectedSlotIds}
              />
            )}
          {parsedMessage?.actions && (
            <Space className="notification-text" wrap>
              {parsedMessage?.actions?.map((item, index) => {
                const isSlotBased =
                  metadata?.frequency &&
                  metadata?.frequencyPeriod === "slot based";
                const isAcceptAction =
                  item.actionType === "acceptCarePlan" ||
                  item.label === "Accept";
                const isDisabled =
                  isSlotBased && isAcceptAction && selectedSlotIds.length === 0;

                return (
                  <PrimaryButton
                    buttonText={item?.label}
                    handleOnClick={() => handleButtonClick(item)}
                    loading={loadingButtons[item?.label] || false}
                    buttonDisable={isDisabled}
                    key={index}
                  />
                );
              })}
            </Space>
          )}
          {data?.createdAt && (
            <div className="notification-time">
              {dateAndTime(data?.createdAt)}
            </div>
          )}
        </div>
      </List.Item>
    </>
  );
};

export default NotificationCard;
